import * as React from 'react'
import { v4 as uuid } from 'uuid'

import { useLogger } from '@thg-commerce/enterprise-core'
import { Figure } from '@thg-commerce/gravity-elements/Figure'
import { SocialEmbed } from '@thg-commerce/enterprise-widget-social-embed'
import { QuestionAndAnswer } from '@thg-commerce/enterprise-widget-question-answer'
import { GlobalLinkCards } from '@thg-commerce/enterprise-widget-global-link-cards'
import { HighlightBox } from '@thg-commerce/enterprise-widget-highlight-box'
import { Recipe } from '@thg-commerce/enterprise-widget-recipe'
import { MacroCalculator } from '@thg-commerce/enterprise-widget-macro-calculator'
import { Disclaimer } from '@thg-commerce/enterprise-widget-disclaimer'

import {
  ProductRecommendationsRenderer,
  validateSkus,
} from '../ProductRecommendationsRenderer'
import { TopNWidgetRenderer } from '../TopNWidgetRenderer'
import { ImageCarouselRenderer } from '../ImageCarouselRenderer'
import { ProductGridRenderer } from '../ProductGridRenderer'
import { PostCardRenderer } from '../PostCardRenderer'
import { TopNItemRenderer } from '../TopNItemRenderer'
import { TutorialRenderer } from '../TutorialRenderer'

import {
  PostContent,
  ElementNode,
  WidgetNode,
  ContainerNode,
  ListElementNode,
  NodeChildren,
} from '../../../post-content'
import {
  StyledGrid,
  StyledImage,
  WidgetGridItem,
  ElementGridItem,
  RecipeGridItem,
} from './styles'
import { RendererContext } from './types'
import { Link, Header, List, Text, Table } from './elements'

interface ArticleBodyRendererProps {
  content: PostContent
  isAmp: boolean
  insetGridItem: {
    colStart: number | number[]
    colSpan: number | number[]
  }
}

const LOG_PREFIX = 'ArticleBodyRenderer: '

const renderElementChildren = (node: ElementNode, context: RendererContext) => {
  if ('children' in node && node.children) {
    return nodeRenderer(node.children, context)
  }
  if (node.props && 'text' in node.props) {
    return node.props.text
  }
  return null
}

const elementTagMap = {
  link: 'a',
}

const elementComponentMap = {
  image: StyledImage,
  link: Link,
  header: Header,
  list: ({
    type,
    items,
    context,
  }: ListElementNode['props'] & {
    context: RendererContext
  }) => List({ type, items, context, nodeRenderer }),
  text: Text,
  table: Table,
}

type OmitDistributive<T, K extends PropertyKey> = T extends any
  ? T extends object
    ? Id<OmitRecursively<T, K>>
    : T
  : never
type Id<T> = {} & { [P in keyof T]: T[P] }
type OmitRecursively<T extends any, K extends PropertyKey> = Omit<
  { [P in keyof T]: OmitDistributive<T[P], K> },
  K
>

const elementNodeRenderer = (
  node: ElementNode,
  context: RendererContext,
  index: number,
) => {
  const elementType =
    elementComponentMap[node.type] || elementTagMap[node.type] || node.type
  let text: string | null = null
  let props: ElementNode['props'] = node.props

  if (node.props && 'text' in node.props) {
    const { text: elementText = null, ...propsWithoutText } = node.props
    text = elementText
    props = propsWithoutText as OmitRecursively<ElementNode['props'], 'text'>
  }

  return React.createElement<
    ElementNode['props'] & { context?: RendererContext; isAmp?: boolean }
  >(
    elementType,
    {
      key: node.type === 'link' ? uuid() : index,
      ...props,
      ...(elementComponentMap[node.type]
        ? { context, isAmp: context.isAmp }
        : {}),
    },
    text ? text : renderElementChildren(node, context),
  )
}

type WidgetDiscriminateUnion<
  Type,
  Key extends keyof Type,
  Value extends Type[Key]
> = Type extends Record<Key, Value> ? Type : never
type WidgetMapDiscriminatedUnion<
  Type extends Record<Key, string>,
  Key extends keyof Type,
  ValueKey extends keyof Type
> = {
  [Value in Type[Key]]: {
    component:
      | React.ComponentType<
          WidgetDiscriminateUnion<Type, Key, Value>[ValueKey] & {
            isAmp: boolean
          }
        >
      | React.ComponentType<WidgetDiscriminateUnion<Type, Key, Value>[ValueKey]>
    shouldRender?: (props: WidgetNode['props']) => boolean
  }
}

const widgetMap: WidgetMapDiscriminatedUnion<WidgetNode, 'type', 'props'> = {
  productRecommendations: {
    component: ProductRecommendationsRenderer,
    shouldRender: validateSkus,
  },
  topN: { component: TopNWidgetRenderer },
  imageCarousel: { component: ImageCarouselRenderer },
  globalLinkCards: { component: GlobalLinkCards },
  media: { component: SocialEmbed },
  productGrid: { component: ProductGridRenderer },
  qa: { component: QuestionAndAnswer },
  postCard: { component: PostCardRenderer },
  topNItem: { component: TopNItemRenderer },
  tutorial: { component: TutorialRenderer },
  figure: { component: Figure },
  highlightBox: { component: HighlightBox },
  recipeDetails: { component: Recipe },
  recipeIngredients: { component: Recipe },
  recipeInstructions: { component: Recipe },
  recipeNutrition: { component: Recipe },
  macronutrientCalculator: { component: MacroCalculator },
  disclaimer: { component: Disclaimer },
}

const widgetNodeRenderer = (
  node: WidgetNode,
  context: RendererContext,
  index: number,
) => {
  if (!widgetMap[node.type]) {
    context.logger.warn(`${LOG_PREFIX}Unsupported widget type ${node.type}`)
    return null
  }

  const shouldRender = widgetMap[node.type].shouldRender
  if (shouldRender ? !shouldRender(node.props) : false) return null

  const widgetChildren =
    node.props && 'children' in node.props
      ? nodeRenderer(node.props.children, context)
      : {}

  return React.createElement<{ isAmp: boolean } & any>(
    widgetMap[node.type].component,
    {
      ...node.props,
      isAmp: context.isAmp,
      key: index,
    },
    widgetChildren,
  )
}

const containerNodeRenderer = (
  node: ContainerNode,
  context: RendererContext,
) => {
  return nodeRenderer(node.children, context)
}

type FindByNodeType<Union, Node> = Union extends { node: Node } ? Union : never
type RenderNodeFunction<Node> = (
  node: FindByNodeType<NodeChildren, Node>,
  context: RendererContext,
  index: number,
) => React.ReactNode

const nodeRendererMap: {
  [Node in NodeChildren['node']]: RenderNodeFunction<Node>
} = {
  element: elementNodeRenderer,
  widget: widgetNodeRenderer,
  container: containerNodeRenderer,
}

type TypeConfig = {
  fullWidth: boolean
}

const widgetTypeConfig: { [type: string]: TypeConfig } = {
  productRecommendations: {
    fullWidth: true,
  },
  globalLinkCards: {
    fullWidth: true,
  },
  productGrid: {
    fullWidth: true,
  },
}

const defaultTypeConfig: TypeConfig = {
  fullWidth: false,
}

const nodeRenderer = (
  nodes: NodeChildren[],
  context: RendererContext,
  gridItem?: {
    colStart: number | number[]
    colSpan: number | number[]
  },
): React.ReactNode[] => {
  let nodeBuffer: React.ReactNode[] = []
  const nodesToRender: React.ReactNode[] = []
  let rowCount: number = 0
  nodes.forEach((node, index) => {
    const { node: nodeRendererType } = node
    const lastNode = index >= nodes.length - 1 ? true : false

    if (!nodeRendererMap[nodeRendererType]) {
      context.logger.warn(
        `${LOG_PREFIX}Unsupported node renderer ${nodeRendererType}`,
      )
      return
    }

    const renderFunction = nodeRendererMap[
      nodeRendererType
    ] as RenderNodeFunction<typeof nodeRendererType>

    const nodeElement = renderFunction(node, context, index)

    const wrapWithStyledGrid = (
      nodeArray: React.ReactNode[],
      isElement: boolean,
    ) => {
      rowCount += 1
      const typeConfig =
        !isElement && 'type' in node && node.type && widgetTypeConfig[node.type]
          ? widgetTypeConfig[node.type]
          : defaultTypeConfig

      const isRecipeWidget =
        'type' in node &&
        (node.type === 'recipeDetails' ||
          node.type === 'recipeIngredients' ||
          node.type === 'recipeInstructions' ||
          node.type === 'recipeNutrition')

      const StyledGridItem = isElement
        ? ElementGridItem
        : isRecipeWidget
        ? RecipeGridItem
        : WidgetGridItem

      return gridItem ? (
        <StyledGridItem
          key={rowCount}
          colStart={typeConfig.fullWidth ? 1 : gridItem?.colStart}
          colSpan={typeConfig.fullWidth ? 12 : gridItem?.colSpan}
          rowStart={rowCount}
        >
          {nodeArray}
        </StyledGridItem>
      ) : (
        <React.Fragment key={rowCount}>{nodeArray}</React.Fragment>
      )
    }

    switch (nodeRendererType) {
      case 'element':
        nodeBuffer.push(nodeElement)
        lastNode && nodesToRender.push(wrapWithStyledGrid(nodeBuffer, true))
        break

      case 'container':
        nodeBuffer.push(nodeElement)
        lastNode && nodesToRender.push(wrapWithStyledGrid(nodeBuffer, true))
        break

      case 'widget':
        let element: React.ReactNode | undefined

        if (nodeBuffer.length > 0) {
          element = wrapWithStyledGrid(nodeBuffer, true)
        }

        const widget: React.ReactNode =
          nodeElement && wrapWithStyledGrid([nodeElement], false)
        element !== undefined
          ? nodesToRender.push(element, widget)
          : nodesToRender.push(widget)
        nodeBuffer = []
        break
    }
  })
  return nodesToRender
}

export const ArticleBodyRenderer = ({
  isAmp,
  content: { content },
  insetGridItem,
}: ArticleBodyRendererProps) => {
  const logger = useLogger()
  return (
    <StyledGrid>
      {nodeRenderer(
        content,
        {
          isAmp,
          logger,
        },
        insetGridItem,
      )}
    </StyledGrid>
  )
}
